/**
 * <sardinia-map> — lazily loads Leaflet + OpenStreetMap only when it nears the
 * viewport, and only on this element. Reads its config from data-* attributes
 * set by sections/sardinia-map.liquid. This is the only place in the theme
 * that references the Leaflet CDN, so no other page is affected.
 */
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

let leafletLoading = null;

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletLoading) return leafletLoading;

  leafletLoading = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return leafletLoading;
}

class SardiniaMap extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#init();
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(this);
  }

  async #init() {
    const canvas = this.querySelector('.sm-canvas');
    if (!canvas) return;

    const lat = Number(this.dataset.lat || 40.12);
    const lng = Number(this.dataset.lng || 9.05);
    const zoom = Number(this.dataset.zoom || 7);
    const markerLat = Number(this.dataset.markerLat || lat);
    const markerLng = Number(this.dataset.markerLng || lng);

    let L;
    try {
      L = await loadLeaflet();
    } catch {
      return;
    }
    if (!L || !this.isConnected) return;

    const map = L.map(canvas, { scrollWheelZoom: false, zoomControl: true, attributionControl: true }).setView(
      [lat, lng],
      zoom
    );

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 12,
      minZoom: 6,
    }).addTo(map);

    const clayIcon = L.divIcon({
      className: '',
      html: '<div style="width:16px;height:16px;border-radius:50%;background:#A85D3B;border:3px solid #F5EFE3;box-shadow:0 2px 8px rgba(42,33,26,.4)"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
    L.marker([markerLat, markerLng], { icon: clayIcon }).addTo(map);

    const pad = 1.6;
    map.setMaxBounds([
      [lat - pad, lng - pad],
      [lat + pad, lng + pad],
    ]);

    setTimeout(() => map.invalidateSize(), 200);
  }
}

customElements.define('sardinia-map', SardiniaMap);
